import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, LoadingSpinner } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useOrganizations } from '@/hooks/admin/useOrganizations';
import { useEntityScreen } from '@/hooks/admin/useEntityScreen';
import { 
  OrganizationDataTable, 
  OrganizationFormModal,
  DesktopOrganizationSidePanel,
  DesktopCreateOrganizationPanel,
} from '@/components/admin/organizations';
import { IOrganizationRowData, IOrganization } from '@/types/admin/organizations';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { ADMIN_COLORS } from '@/constants/admin';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type OrganizationsScreenProps = StackScreenProps<AdminStackParamList, 'Organizations'>;

export const OrganizationsScreen: React.FC<OrganizationsScreenProps> = ({
  navigation,
}) => {
  const { showApiError } = useErrorToast();
  const showSearch = false;

  const {
    data,
    tableRows, // Full organization objects
    tableRowsData, // Filtered display data
    isLoading,
    error,
    enhancedError,
    searchQuery,
    updateSearchQuery,
    refresh,
  } = useOrganizations();

  // Use entity screen hook for CSV and layout management
  const {
    csvData,
    layoutMode,
    formatCsvData,
  } = useEntityScreen<IOrganizationRowData>({
    csvHeaders: ['Name', 'Domain', 'Created At'],
    formatCsvRow: (row) => [row.name, row.domain, row.createdAt || 'N/A'],
  });

  // Panel states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<IOrganization | null>(null);

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  // Handle organization selection
  const handleOrganizationSelect = useCallback((organization: IOrganizationRowData) => {
    // Find full organization data (tableRows contains full IOrganization objects)
    const fullOrg = tableRows.find(row => row.id === organization.id);
    if (fullOrg) {
      setSelectedOrganization(fullOrg);
      
      // Use side panel on desktop, modal on mobile
      if (layoutMode === 'desktop') {
        setShowEditPanel(true);
      } else {
        setShowFormModal(true);
      }
    }
  }, [tableRows, layoutMode]);

  // Handle add organization
  const handleAddOrganization = useCallback(() => {
    setSelectedOrganization(null);
    
    // Use side panel on desktop, modal on mobile
    if (layoutMode === 'desktop') {
      setShowCreatePanel(true);
    } else {
      setShowFormModal(true);
    }
  }, [layoutMode]);

  // Handle success (refresh and close)
  const handleSuccess = useCallback(() => {
    refresh();
    setShowFormModal(false);
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedOrganization(null);
  }, [refresh]);

  // Handle close
  const handleClose = useCallback(() => {
    setShowFormModal(false);
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedOrganization(null);
  }, []);

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopAdminLayout>
          <View style={styles.desktopContainer}>
            <DesktopPageHeader
              title="Organizations"
              subtitle=""
              showAddButton={true}
              onAddPress={handleAddOrganization}
              addButtonText="Create Organization"
            />
            
            <AdminErrorBoundary>
              <DataTableProvider 
                tableKey="organizations"
                defaults={{
                  sortModel: [{ field: 'name', sort: 'asc' }],
                }}
              >
                <OrganizationDataTable
                  data={data}
                  isLoading={isLoading}
                  onOrganizationSelect={handleOrganizationSelect}
                  updateCsvData={formatCsvData}
                  showSearch={showSearch}
                />
              </DataTableProvider>
            </AdminErrorBoundary>
          </View>
        </DesktopAdminLayout>

        {/* Desktop Create Panel */}
        <DesktopCreateOrganizationPanel
          visible={showCreatePanel}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />

        {/* Desktop Edit Panel */}
        {selectedOrganization && (
          <DesktopOrganizationSidePanel
            visible={showEditPanel}
            onClose={handleClose}
            organization={selectedOrganization}
            onSuccess={handleSuccess}
          />
        )}
      </>
    );
  }

  // Determine if we have data
  const hasData = data.count > 0;
  
  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData;

  // Mobile layout
  return (
    <AdminErrorBoundary context="OrganizationsScreen">
      <BaseAdminScreen
        screenName="Organizations"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Organizations"
          emptyStateType="organization"
          onAddPress={handleAddOrganization}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
        >
          {showLoadingState ? (
            <LoadingSpinner message="Loading organizations..." />
          ) : (
            <DataTableProvider 
              tableKey="organizations"
              defaults={{
                sortModel: [{ field: 'name', sort: 'asc' }],
              }}
            >
              <View style={styles.dataContainer}>
                <OrganizationDataTable
                  data={data}
                  isLoading={isLoading}
                  onOrganizationSelect={handleOrganizationSelect}
                  updateCsvData={formatCsvData}
                  showSearch={showSearch}
                />
              </View>
            </DataTableProvider>
          )}
        </AdminEmptyScreenLayout>

        {/* Mobile Modal (for creating or editing) */}
        <OrganizationFormModal
          visible={showFormModal}
          onClose={handleClose}
          organization={selectedOrganization}
          onSuccess={handleSuccess}
        />
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  desktopContainer: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  dataContainer: {
    flex: 1,
    width: '100%',
  },
});

